/**
 * @fileoverview The signed-in user's identity for the profile modal.
 * @description Reads GET /identity/me through useCurrentSession and falls back to the auth store's
 * own email, name and role while that resolves (or when the backend has none on file), so the
 * profile modal always has something to show. Split out of ProfileModalContent to keep that
 * component small.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useCurrentSession } from '@/features/auth/hooks/useCurrentSession'
import { initialsFromName } from '@/features/auth/utils/profile-display.utils'
import { useAuth } from '@/shared/lib/auth/useAuth'
import { initialsFromEmail } from '@/shared/lib/auth/user-display.utils'

import type { CurrentPrincipal } from '@/features/auth/services/session.service'

interface ProfileIdentity {
  principal: CurrentPrincipal | undefined
  email: string
  displayName: string
  roleName: string | undefined
  initials: string
}

/**
 * @description Resolves the identity the profile modal shows.
 * @returns {ProfileIdentity} The principal (when loaded), email, display name, role name and avatar
 * initials.
 */
export function useProfileIdentity(): ProfileIdentity {
  const { user } = useAuth()
  const principal = useCurrentSession().data
  const email = principal?.email ?? user?.email ?? ''
  const name = principal?.displayName ?? user?.name ?? ''

  return {
    principal,
    email,
    displayName: name.trim() || email,
    roleName: principal?.activeRoleName ?? user?.activeRoleName,
    initials: initialsFromName(name) || initialsFromEmail(email),
  }
}
